import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchPublishedResearchTools,
  filterResearchTools,
  deriveResearchToolFacets,
  EMPTY_RESEARCH_TOOL_FILTERS,
  RESEARCH_TOOL_TYPES,
} from '../lib/researchTools.js';
import { useAuth } from '../context/AuthContext.jsx';
import { loadWithRetry } from '../lib/loadWithRetry.js';
import ResearchToolCard from '../components/researchTools/ResearchToolCard.jsx';
import ResearchToolDetailModal from '../components/researchTools/ResearchToolDetailModal.jsx';

// Community-maintained list of archives, digital libraries, and online
// editions (research_tools table, migration 020). Same submit → approve
// workflow as the other modules.
export default function ResearchTools() {
  const { t } = useTranslation();
  const { isActive } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY_RESEARCH_TOOL_FILTERS);
  const [detail, setDetail] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    loadWithRetry(fetchPublishedResearchTools, { label: 'research-tools' })
      .then((rows) => !cancelled && setItems(rows))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  // Deep link from a What's New notice: /research-tools?entry=<id>.
  useEffect(() => {
    const id = searchParams.get('entry');
    if (!id || !items) return;
    const match = items.find((m) => m.id === id);
    if (match) setDetail(match);
  }, [items, searchParams]);

  const facets = useMemo(() => (items ? deriveResearchToolFacets(items) : null), [items]);
  const results = useMemo(() => {
    if (!items) return [];
    return filterResearchTools(items, { ...filters, query });
  }, [items, filters, query]);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
        <button
          type="button"
          className="btn-primary mt-6"
          onClick={() => setRetryKey((k) => k + 1)}
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }
  if (!items || !facets) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-shell px-6 pb-20 pt-28">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label">{t('researchTools.label')}</div>
          <h1 className="font-serif text-4xl font-semibold text-cream">
            {t('researchTools.heading')}
          </h1>
          <p className="mt-2 max-w-2xl text-ink-secondary">
            {t('researchTools.subheading', { count: items.length })}
          </p>
        </div>
        {isActive && (
          <Link to="/research-tools/new" className="btn-primary">
            {t('researchTools.add')}
          </Link>
        )}
      </header>

      <div className="mb-6 flex flex-wrap gap-2 border-b border-white/10 pb-3">
        <TypeTab
          active={filters.type === ''}
          label={t('researchTools.types.all')}
          count={items.length}
          onClick={() => setFilters({ ...filters, type: '' })}
        />
        {RESEARCH_TOOL_TYPES.map((type) => {
          const facet = facets.types.find((x) => x.type === type);
          const count = facet?.count ?? 0;
          if (count === 0) return null;
          return (
            <TypeTab
              key={type}
              active={filters.type === type}
              label={t(`researchTools.types.${type}`)}
              count={count}
              onClick={() => setFilters({ ...filters, type })}
            />
          );
        })}
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-navy-base/50 p-3 backdrop-blur">
        <input
          type="search"
          placeholder={t('researchTools.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2 text-cream placeholder:text-ink-muted focus:border-gold/50"
        />
      </div>

      <p className="mb-4 text-sm text-ink-secondary">
        {t('researchTools.resultCount', { count: results.length })}
      </p>

      {results.length === 0 ? (
        <p className="rounded-lg border border-white/10 bg-navy-base/40 p-10 text-center text-ink-secondary">
          {items.length === 0 ? t('researchTools.emptyAll') : t('researchTools.empty')}
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {results.map((m) => (
            <ResearchToolCard key={m.id} item={m} onOpen={setDetail} />
          ))}
        </div>
      )}

      {!isActive && (
        <p className="mt-10 text-sm text-ink-secondary">{t('researchTools.suggest')}</p>
      )}

      <ResearchToolDetailModal
        item={detail}
        onClose={() => setDetail(null)}
        onDeleted={(id) => setItems((prev) => prev?.filter((m) => m.id !== id) ?? prev)}
      />
    </div>
  );
}

function TypeTab({ active, label, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition ${
        active
          ? 'border-gold/60 bg-navy-mid/60 text-gold-light'
          : 'border-white/10 bg-navy-deep/40 text-ink-secondary hover:border-gold/30'
      }`}
    >
      {label}
      <span className="rounded-full bg-navy-deep/60 px-2 text-[0.7rem] text-ink-muted">{count}</span>
    </button>
  );
}
